/**
 * Shared Relationship Options
 *
 * Single source of truth for all relationship dropdown options across the app.
 * Used by: EmergencyContactFields, AuthorizedPickupFields, AuthorizedPickupCard,
 *          AuthorizedPickupSection, AuthorizedPickupInfo, AthleteRelationshipField,
 *          ParentRelationshipField, AthleteSelectionModal, registration/types
 */

export interface RelationshipOption {
  value: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Emergency Contact — used when a parent/staff picks an emergency contact
// for an athlete (e.g. EmergencyContactFields, AthleteRelationshipField)
// ---------------------------------------------------------------------------
export const EMERGENCY_CONTACT_RELATIONSHIPS: RelationshipOption[] = [
  { value: 'parent', label: 'Parent/Guardian' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'other_family', label: 'Other Family Member' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
];

/** Same list but with a blank "Select…" placeholder as the first entry. */
export const EMERGENCY_CONTACT_RELATIONSHIPS_WITH_PLACEHOLDER: RelationshipOption[] = [
  { value: '', label: 'Select Relationship' },
  ...EMERGENCY_CONTACT_RELATIONSHIPS,
];

// ---------------------------------------------------------------------------
// Authorized Pickup — who can pick up the athlete from practice/events
// ---------------------------------------------------------------------------
export const AUTHORIZED_PICKUP_RELATIONSHIPS: RelationshipOption[] = [
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Legal Guardian' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other_relative', label: 'Other Relative' },
  { value: 'family_friend', label: 'Family Friend' },
  { value: 'other', label: 'Other' },
];

export const AUTHORIZED_PICKUP_RELATIONSHIPS_WITH_PLACEHOLDER: RelationshipOption[] = [
  { value: '', label: 'Select Relationship' },
  ...AUTHORIZED_PICKUP_RELATIONSHIPS,
];

// ---------------------------------------------------------------------------
// Parent Emergency Contact — when a parent fills out *their own* emergency
// contact (includes spouse, neighbor, coworker, etc.)
// ---------------------------------------------------------------------------
export const PARENT_EMERGENCY_CONTACT_RELATIONSHIPS: RelationshipOption[] = [
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'parent', label: 'Parent/Guardian' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'child', label: 'Adult Child' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'other_family', label: 'Other Family Member' },
  { value: 'friend', label: 'Friend' },
  { value: 'neighbor', label: 'Neighbor' },
  { value: 'coworker', label: 'Coworker' },
  { value: 'other', label: 'Other' },
];

export const PARENT_EMERGENCY_CONTACT_RELATIONSHIPS_WITH_PLACEHOLDER: RelationshipOption[] = [
  { value: '', label: 'Select Relationship' },
  ...PARENT_EMERGENCY_CONTACT_RELATIONSHIPS,
];

// ---------------------------------------------------------------------------
// Parent-to-Athlete — how a parent is related to the athlete
// (used in AthleteSelectionModal when linking parents to athletes)
// ---------------------------------------------------------------------------
export const PARENT_TO_ATHLETE_RELATIONSHIPS: RelationshipOption[] = [
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'stepparent', label: 'Step-parent' },
  { value: 'other_family', label: 'Other Family Member' },
  { value: 'emergency_contact', label: 'Emergency Contact' },
  { value: 'coach', label: 'Coach' },
  { value: 'staff', label: 'Staff Member' },
];

// ---------------------------------------------------------------------------
// Utility: build a value → label lookup from any option list
// ---------------------------------------------------------------------------
export function buildRelationshipLabelMap(
  options: RelationshipOption[]
): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

/**
 * Format a stored relationship value into a human-readable label.
 * Falls back to title-casing the raw value if not found in the map.
 */
export function formatRelationshipLabel(
  value: string,
  options: RelationshipOption[] = AUTHORIZED_PICKUP_RELATIONSHIPS
): string {
  if (!value) return '';
  const map = buildRelationshipLabelMap(options);
  return (
    map[value] ??
    value
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}
